// @flow
import * as React from 'react';
import {
    Column,
    Columns,
    Divider,
    Heading,
    Stack,
    Text
} from '@locus-taxy/neo-ui';
import { VolumeUnitEnum } from 'common/enums/VolumeUnitEnum';
import { units } from './units';
import { VehicleCompartment } from 'common/models/internal/VehicleCompartment';
import type { TFunction } from 'react-i18next';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import { dashboardColors } from 'common/enums/ColorEnum';

const Label = ({ children }) => {
    return (
        <Text color="rgba(52, 57, 71, 0.5)" fontWeight="600" fontSize={0}>
            {children}
        </Text>
    );
};

const Value = ({ children }) => {
    return (
        <Text color={dashboardColors.dark} fontSize={1}>
            {children}
        </Text>
    );
};

const Property = ({ label, value, unit = '' }) => {
    return (
        <Stack space="tiny">
            <Label>{label}</Label>
            <Value>{value ? `${value} ${unit}` : '-'}</Value>
        </Stack>
    );
};

type Props = {
    compartment: VehicleCompartment,
    title: string,
    t: TFunction,
    unitConverter: UnitConversionHelper
};

export const Compartment = ({
    compartment,
    title,
    t,
    unitConverter
}: Props): React.Node => {
    const _t = React.useCallback(
        (key: string) => {
            return t(`masterVehicleModelProperties.compartments.${key}`);
        },
        [t]
    );

    const getCapacityUnits = React.useCallback(
        compartment => {
            const volumeUnit =
                compartment.volume.unit === VolumeUnitEnum.MetersCubic
                    ? unitConverter.getConvertedUnitDisplayText({
                          preferredUnits: units.volume.preferredUnits,
                          sourceUnit: units.volume.sourceUnit,
                          pluralizeUnit: false
                      })
                    : '';

            const dimensionUnit = unitConverter.getConvertedUnitDisplayText({
                preferredUnits: units.dimensions.preferredUnits,
                sourceUnit: units.dimensions.sourceUnit,
                pluralizeUnit: false
            });

            return { volumeUnit, dimensionUnit };
        },
        [unitConverter]
    );

    const { dimensionUnit, volumeUnit } = getCapacityUnits(compartment);

    return (
        <Stack space="small" _sx={{ p: 2 }}>
            <Heading size="intermediate">{title}</Heading>
            <Stack space="small">
                <Property label={_t('id')} value={`${compartment.id}`} />
                <Columns space="small">
                    <Column>
                        <Property
                            label={_t('isMandatory')}
                            value={
                                !compartment.optional
                                    ? t('translations:yes')
                                    : t('translations:no')
                            }
                        />
                    </Column>
                    <Column>
                        <Property
                            label={_t('priority')}
                            value={`${compartment.priority}`}
                        />
                    </Column>
                </Columns>
            </Stack>
            <Divider color="grey.100" width="fluid" />
            <Stack space="small">
                <Columns space="small">
                    <Column>
                        <Property
                            label={_t('dimensions.length')}
                            value={compartment.dimensions.length}
                            unit={dimensionUnit}
                        />
                    </Column>
                    <Column>
                        <Property
                            label={_t('dimensions.breadth')}
                            value={compartment.dimensions.breadth}
                            unit={dimensionUnit}
                        />
                    </Column>
                    <Column>
                        <Property
                            label={_t('dimensions.height')}
                            value={compartment.dimensions.height}
                            unit={dimensionUnit}
                        />
                    </Column>
                </Columns>
                <Columns space="small">
                    <Column>
                        <Property
                            label={_t('volume')}
                            value={compartment.volume.value}
                            unit={volumeUnit}
                        />
                    </Column>
                </Columns>
            </Stack>
        </Stack>
    );
};

export default Compartment;
